import { Button } from "@/components/button";
import { Calendar } from "@/components/calendar";
import { Combobox } from "@/components/combobox";
import { Editor } from "@/components/editor";
import { Input } from "@/components/input";
import { Label } from "@/components/label";
import { MultiSelect } from "@/components/multi-select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/tooltip";
import {
  useAudienceAndCampaignOptions,
  useCreateExperiment,
  usePredictionFlightOptions,
  useUpdateExperiment,
  useUsers,
  useVertexMonitoredModels,
} from "@/http/api-hooks";
import {
  CampaignType,
  Experiment,
  ExperimentCampaign,
  ExperimentImportMetadata,
  ExperimentOutcome,
  ExperimentScopeFilter,
  ExperimentType,
  ExperimentVariant,
} from "@/http/api-types";
import { CalendarIcon, InfoCircledIcon, TrashIcon } from "@radix-ui/react-icons";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import {
  Controller,
  FormProvider,
  useForm,
  useWatch,
  type ControllerRenderProps,
  type UseFormReturn,
} from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  buildExperimentRequest,
  extractBackendFieldErrors,
  toUTCCalendarDate,
  validateExperimentFormValues,
} from "./experiment-form-helpers";
import { EXPERIMENT_FORM_LABEL_CLASS } from "./experiment-form-styles";
import { ExperimentVariants } from "./ExperimentVariants";
import { GlobalExperimentImportSection } from "./GlobalExperimentImportSection";
import { HYPOTHESIS_TEMPLATE } from "./hypothesis-template";
import { useExperimentFormModel } from "./use-experiment-form-model";
import { useExperimentVariantParams } from "./use-experiment-variant-params";

export type CampaignParamForm = {
  paramName: string;
  paramValue: string;
};

export type PredictionFlightParamForm = {
  predictorTypeId: number | null;
  paramValue: string;
};

type CampaignForm = {
  campaignId: string;
  controlCampaignId: string;
  // 1-based test-group index for test campaigns (undefined for controls). Tests at the same index
  // across controls form one test group.
  testGroup?: number;
  variant: ExperimentVariant;
  trafficAllocation: number;
  params: CampaignParamForm[];
};

type PredictionFlightForm = {
  predictionFlightId: string;
  variant: ExperimentVariant;
  trafficAllocation: number;
  params: PredictionFlightParamForm[];
};

type ExternalLinkForm = {
  url: string;
};

export type ExperimentFormValues = {
  experimentType: ExperimentType;
  name: string;
  hypothesis: string;
  conclusion: string;
  outcome: ExperimentOutcome | "";
  rollOutAdditionalInfo: string;
  owner: string;
  audiences: string[];
  importedGlobalExperimentId: string;
  importLastSyncedAt: string;
  scopeFilters: ExperimentScopeFilter[];
  importMetadata: ExperimentImportMetadata | null;
  startDate: Date | null;
  startTime: string;
  endDate: Date | null;
  endTime: string;
  externalLinks: ExternalLinkForm[];
  campaigns: CampaignForm[];
  predictionFlights: PredictionFlightForm[];
  repoBranchPairs: RepoBranchPairForm[];
};

export type RepoBranchPairForm = {
  repo: string;
  branch: string;
};

export const DEFAULT_CAMPAIGNS: CampaignForm[] = [
  { campaignId: "", controlCampaignId: "", variant: "control", trafficAllocation: 5000, params: [] },
  { campaignId: "", controlCampaignId: "", testGroup: 1, variant: "test", trafficAllocation: 5000, params: [] },
];

export const DEFAULT_PREDICTION_FLIGHTS: PredictionFlightForm[] = [
  { predictionFlightId: "", variant: "test", trafficAllocation: 5000, params: [] },
  { predictionFlightId: "", variant: "control", trafficAllocation: 5000, params: [] },
];

const CUSTOM_EXPERIMENT_CAMPAIGN_TYPE: CampaignType = "all";

export function parseTimeFromDate(date: Date): string {
  return `${date.getUTCHours().toString().padStart(2, "0")}:${date.getUTCMinutes().toString().padStart(2, "0")}`;
}

function getExistingExperimentDefaults(
  initialExperiment: Experiment,
  initialCampaigns: ExperimentCampaign[] | undefined,
): Pick<ExperimentFormValues, "startDate" | "endDate" | "campaigns" | "predictionFlights"> {
  const startDate = toUTCCalendarDate(initialExperiment.start_date);
  const endDate = toUTCCalendarDate(initialExperiment.end_date);

  const campaigns =
    initialCampaigns && initialCampaigns.length > 0
      ? initialCampaigns.map(c => ({
          campaignId: c.campaign_id.toString(),
          controlCampaignId: c.control_campaign_id?.toString() || "",
          testGroup: c.test_group ?? undefined,
          variant: c.variant,
          trafficAllocation: c.traffic_allocation,
          params: (c.params || []).map(p => ({
            paramName: p.param_name,
            paramValue: p.param_value,
          })),
        }))
      : [...DEFAULT_CAMPAIGNS];

  const predictionFlights =
    initialExperiment.prediction_flights && initialExperiment.prediction_flights.length > 0
      ? initialExperiment.prediction_flights.map(pf => ({
          predictionFlightId: pf.prediction_flight_id.toString(),
          variant: pf.variant,
          trafficAllocation: pf.traffic_allocation,
          params: (pf.params || []).map(p => ({
            predictorTypeId: p.predictor_type_id,
            paramValue: p.param_value,
          })),
        }))
      : [...DEFAULT_PREDICTION_FLIGHTS];

  return { startDate, endDate, campaigns, predictionFlights };
}

export function getExperimentFormDefaultValues(
  initialExperiment: Experiment | undefined,
  initialCampaigns: ExperimentCampaign[] | undefined,
): ExperimentFormValues {
  if (!initialExperiment) {
    return {
      experimentType: ExperimentType.Global,
      name: "",
      hypothesis: HYPOTHESIS_TEMPLATE,
      conclusion: "",
      outcome: "",
      rollOutAdditionalInfo: "",
      owner: "",
      audiences: [],
      importedGlobalExperimentId: "",
      importLastSyncedAt: "",
      scopeFilters: [],
      importMetadata: null,
      startDate: null,
      startTime: "00:00",
      endDate: null,
      endTime: "23:59",
      externalLinks: [],
      campaigns: [...DEFAULT_CAMPAIGNS],
      predictionFlights: [...DEFAULT_PREDICTION_FLIGHTS],
      repoBranchPairs: [],
    };
  }

  const { startDate, endDate, campaigns, predictionFlights } = getExistingExperimentDefaults(
    initialExperiment,
    initialCampaigns,
  );

  return {
    experimentType: initialExperiment.type || ExperimentType.Custom,
    name: initialExperiment.name || "",
    hypothesis: initialExperiment.hypothesis || "",
    conclusion: initialExperiment.conclusion || "",
    outcome: initialExperiment.outcome || "",
    rollOutAdditionalInfo: initialExperiment.rollout_additional_info || "",
    owner: initialExperiment.owner ? initialExperiment.owner.toString() : "",
    audiences: (initialExperiment.audiences || []).map(id => id.toString()),
    importedGlobalExperimentId: initialExperiment.imported_global_experiment_id?.toString() || "",
    importLastSyncedAt: initialExperiment.import_last_synced_at || "",
    scopeFilters: initialExperiment.scope_filters || [],
    importMetadata: initialExperiment.import_metadata || null,
    startDate,
    startTime: initialExperiment.start_date ? parseTimeFromDate(new Date(initialExperiment.start_date)) : "00:00",
    endDate,
    endTime: initialExperiment.end_date ? parseTimeFromDate(new Date(initialExperiment.end_date)) : "23:59",
    externalLinks: (initialExperiment.external_links || []).map(link => ({ url: link })),
    campaigns,
    predictionFlights,
    repoBranchPairs: (initialExperiment.repo_branch_pairs || []).map(pair => ({
      repo: pair.repo,
      branch: pair.branch,
    })),
  };
}

type ExperimentFormProps = {
  experimentId: number | null;
  isEditing: boolean;
  initialExperiment?: Experiment;
  initialCampaigns?: ExperimentCampaign[];
  onSaveSuccess?: () => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
  formMethods?: UseFormReturn<ExperimentFormValues>;
  showExperimentTypeField?: boolean;
};

type ExperimentFormInnerProps = Omit<ExperimentFormProps, "formMethods">;

function ExperimentFormInner({
  experimentId,
  isEditing,
  onSaveSuccess,
  onCancel,
  showCancelButton = true,
  showExperimentTypeField = true,
}: ExperimentFormInnerProps) {
  const navigate = useNavigate();
  const [startPopoverOpen, setStartPopoverOpen] = useState(false);
  const [endPopoverOpen, setEndPopoverOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { data: users, isLoading: usersLoading } = useUsers();
  const { data: predictionFlightsData, isLoading: predictionFlightsLoading } = usePredictionFlightOptions();
  const { models: monitoredModels } = useVertexMonitoredModels();
  const { data: audienceCampaignData, isLoading: audienceOptionsLoading } = useAudienceAndCampaignOptions({
    campaign_type: CUSTOM_EXPERIMENT_CAMPAIGN_TYPE,
  });
  const { trigger: createTrigger, isMutating: isCreating } = useCreateExperiment();
  const { trigger: updateTrigger, isMutating: isUpdating } = useUpdateExperiment(experimentId || 0);

  const {
    control,
    setValue,
    handleSubmit,
    isSubmitting,
    isDirty,
    experimentType,
    audiences,
    campaigns,
    predictionFlights,
    externalLinks,
    importMetadata,
    campaignFields,
    appendCampaign,
    removeCampaign,
    replaceCampaigns,
    predictionFlightFields,
    appendPredictionFlight,
    removePredictionFlight,
    replacePredictionFlights,
    externalLinkFields,
    appendExternalLink,
    removeExternalLink,
    repoBranchPairs,
    repoBranchPairFields,
    appendRepoBranchPair,
    removeRepoBranchPair,
    audienceOptions,
    audienceOptionsRaw,
    campaignOptions,
    organizationIds,
    predictionFlightOptions,
    userOptions,
    totalCampaignTrafficAllocation,
  } = useExperimentFormModel({
    users,
    audienceCampaignData,
    predictionFlightsData,
  });

  const isLoading = usersLoading || audienceOptionsLoading || predictionFlightsLoading;
  const isSaving = isCreating || isUpdating || isSubmitting;

  const handleExperimentTypeValueChange = (
    value: string,
    currentValue: ExperimentType,
    onChange: ControllerRenderProps<ExperimentFormValues, "experimentType">["onChange"],
  ) => {
    const nextExperimentType = parseInt(value, 10) as ExperimentType;
    const didChange = currentValue !== nextExperimentType;
    onChange(nextExperimentType);

    if (!isEditing && didChange) {
      if (nextExperimentType === ExperimentType.Custom) {
        replaceCampaigns([...DEFAULT_CAMPAIGNS]);
      } else {
        replacePredictionFlights([...DEFAULT_PREDICTION_FLIGHTS]);
      }
      setValue("importedGlobalExperimentId", "", { shouldDirty: true });
      setValue("importLastSyncedAt", "", { shouldDirty: true });
      setValue("scopeFilters", [], { shouldDirty: true });
      setValue("importMetadata", null, { shouldDirty: true });
      setFieldErrors({});
    }
  };

  const handleAudiencesChange = (
    nextValues: string[],
    currentValues: string[],
    onChange: ControllerRenderProps<ExperimentFormValues, "audiences">["onChange"],
  ) => {
    const didChange = nextValues.length !== currentValues.length || nextValues.some((v, i) => v !== currentValues[i]);
    onChange(nextValues);

    if (!didChange || experimentType !== ExperimentType.Custom) return;

    setFieldErrors({});

    if (!isEditing) {
      replaceCampaigns([...DEFAULT_CAMPAIGNS]);
      return;
    }

    // Editing: prune campaigns that no longer belong to the selected audiences,
    // preserving valid entries (including empty slots) so the user keeps their work.
    const validCampaignIds = new Set<string>();
    nextValues.forEach(audienceId => {
      audienceOptionsRaw?.[audienceId]?.campaigns.forEach(campaign => {
        validCampaignIds.add(campaign.id.toString());
      });
    });
    const prunedCampaigns = campaigns.filter(c => !c.campaignId || validCampaignIds.has(c.campaignId));
    if (prunedCampaigns.length !== campaigns.length) {
      replaceCampaigns(prunedCampaigns.length > 0 ? prunedCampaigns : [...DEFAULT_CAMPAIGNS]);
    }
  };

  const {
    addCampaignParam,
    removeCampaignParam,
    updateCampaignParam,
    addPredictionFlightParam,
    removePredictionFlightParam,
    updatePredictionFlightParamValue,
    updatePredictionFlightParamType,
  } = useExperimentVariantParams({
    campaigns,
    predictionFlights,
    setValue,
  });

  const watchedValues = useWatch({ control }) as ExperimentFormValues;
  const validCampaignIds = useMemo(() => new Set(campaignOptions.map(c => c.id.toString())), [campaignOptions]);

  // Reactive validation drives both the disabled Save button and the tooltip that explains why
  // it is disabled, so the two can never disagree.
  const validationErrors = useMemo(
    () => validateExperimentFormValues(watchedValues, { organizationIds, validCampaignIds }),
    [watchedValues, organizationIds, validCampaignIds],
  );
  const hasBlockingErrors = Object.keys(validationErrors).length > 0;
  const saveBlockReasons = useMemo(() => {
    const reasons = Array.from(new Set(Object.values(validationErrors)));
    return isEditing && !isDirty ? ["No changes to save yet", ...reasons] : reasons;
  }, [validationErrors, isEditing, isDirty]);

  // New global experiments are import-first: until a source is imported, only the experiment type
  // selector and the import section are shown. The rest of the form appears once imported. Custom
  // experiments and any experiment being edited show the full form immediately.
  const showRestOfForm =
    isEditing || experimentType !== ExperimentType.Global || !!watchedValues.importedGlobalExperimentId;

  const handleSave = handleSubmit(async values => {
    setFieldErrors({});
    const newFieldErrors = validateExperimentFormValues(values, {
      organizationIds,
      validCampaignIds,
    });

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      toast.error("Validation failed", { description: "Please fix the highlighted errors" });
      return;
    }

    const experimentData = buildExperimentRequest(values, {
      organizationIds,
    });

    const action = isEditing ? "update" : "create";
    try {
      if (isEditing && experimentId) {
        await updateTrigger(experimentData);
        toast.success("Experiment updated successfully", { description: `"${values.name}" has been updated.` });
      } else {
        await createTrigger(experimentData);
        toast.success("Experiment created successfully", { description: `"${values.name}" has been created.` });
      }

      if (onSaveSuccess) {
        onSaveSuccess();
      } else {
        navigate("/experiments");
      }
    } catch (error: unknown) {
      console.error("Failed to save experiment:", error);
      const backendFieldErrors = extractBackendFieldErrors(error);
      if (backendFieldErrors) {
        setFieldErrors(backendFieldErrors);
        toast.error("Validation failed", { description: "Please fix the highlighted errors" });
      } else {
        toast.error(`Failed to ${action} experiment`, {
          description: "Please reach out to dev team if the problem persists.",
        });
      }
    }
  });

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate("/experiments");
    }
  };

  return (
    <div className="mx-auto grid w-full max-w-4xl gap-6">
      {showExperimentTypeField && (
        <div>
          <div className="flex items-center gap-1 mb-2">
            <Label className={EXPERIMENT_FORM_LABEL_CLASS}>Experiment Type</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoCircledIcon className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-sm">
                    <strong>Global:</strong> Uses prediction flights for experiment traffic allocation. No audience
                    selection required.
                  </p>
                  <p className="text-sm mt-1">
                    <strong>Custom:</strong> Uses campaigns with a user ID split based traffic separation.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Controller
            control={control}
            name="experimentType"
            render={({ field }) => (
              <Select
                value={field.value.toString()}
                onValueChange={value => handleExperimentTypeValueChange(value, field.value, field.onChange)}
                disabled={isEditing}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ExperimentType.Global.toString()}>Global</SelectItem>
                  <SelectItem value={ExperimentType.Custom.toString()}>Custom</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>
      )}

      {experimentType === ExperimentType.Global && !isEditing && (
        <GlobalExperimentImportSection
          isEditing={isEditing}
          parseTimeFromDate={parseTimeFromDate}
          replacePredictionFlights={replacePredictionFlights}
        />
      )}

      {showRestOfForm && (
        <>
          <div>
            <Label htmlFor="name" className={EXPERIMENT_FORM_LABEL_CLASS}>
              Experiment Name
            </Label>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <Input
                  id="name"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter experiment name"
                  className={fieldErrors.name ? "border-destructive" : ""}
                />
              )}
            />
            {fieldErrors.name && <p className="text-sm text-destructive mt-1">{fieldErrors.name}</p>}
          </div>

          <div>
            <Label htmlFor="hypothesis" className={EXPERIMENT_FORM_LABEL_CLASS}>
              Hypothesis
            </Label>
            <div className={fieldErrors.hypothesis ? "border border-destructive rounded-md" : ""}>
              <Controller
                control={control}
                name="hypothesis"
                render={({ field }) => (
                  <Editor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Type / for commands"
                    experimentId={experimentId?.toString()}
                  />
                )}
              />
            </div>
            {fieldErrors.hypothesis && <p className="text-sm text-destructive mt-1">{fieldErrors.hypothesis}</p>}
          </div>

          <div>
            <Label className={EXPERIMENT_FORM_LABEL_CLASS}>Owner</Label>
            <Controller
              control={control}
              name="owner"
              render={({ field }) => (
                <Combobox
                  value={field.value || ""}
                  setValue={field.onChange}
                  options={userOptions}
                  buttonPlaceholder="Select owner"
                  searchPlaceholder="Search users"
                  notFoundMessage="No users found"
                  isLoading={usersLoading}
                  disabled={false}
                />
              )}
            />
            {fieldErrors.owner && <p className="text-sm text-destructive mt-1">{fieldErrors.owner}</p>}
          </div>

          {experimentType === ExperimentType.Custom && (
            <div>
              <Label className={EXPERIMENT_FORM_LABEL_CLASS}>Audiences</Label>
              <Controller
                control={control}
                name="audiences"
                render={({ field }) => (
                  <MultiSelect
                    value={field.value}
                    onValueChange={nextValues => handleAudiencesChange(nextValues, field.value, field.onChange)}
                    options={audienceOptions}
                    placeholder="Select audiences"
                    disabled={audienceOptionsLoading}
                  />
                )}
              />
              {(fieldErrors.audiences || fieldErrors.organizations) && (
                <p className="text-sm text-destructive mt-1">{fieldErrors.audiences || fieldErrors.organizations}</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={EXPERIMENT_FORM_LABEL_CLASS}>Start</Label>
              <div className="flex gap-2">
                <Controller
                  control={control}
                  name="startDate"
                  render={({ field }) => (
                    <Popover open={startPopoverOpen} onOpenChange={setStartPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          id="start-date"
                          variant="outline"
                          className={`flex-1 justify-start text-left font-normal ${!field.value ? "text-muted-foreground" : ""}`}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {field.value ? format(field.value, "LLL dd, y") : <span>Pick start date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value || undefined}
                          onDayClick={day => {
                            field.onChange(day);
                            setStartPopoverOpen(false);
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                />
                <div className="relative">
                  <Controller
                    control={control}
                    name="startTime"
                    render={({ field }) => (
                      <Input type="time" value={field.value} onChange={field.onChange} className="w-32 pr-12" />
                    )}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                    UTC
                  </span>
                </div>
              </div>
              {fieldErrors.start_date && <p className="text-sm text-destructive mt-1">{fieldErrors.start_date}</p>}
            </div>

            <div>
              <Label className={EXPERIMENT_FORM_LABEL_CLASS}>End</Label>
              <div className="flex gap-2">
                <Controller
                  control={control}
                  name="endDate"
                  render={({ field }) => (
                    <Popover open={endPopoverOpen} onOpenChange={setEndPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          id="end-date"
                          variant="outline"
                          className={`flex-1 justify-start text-left font-normal ${!field.value ? "text-muted-foreground" : ""}`}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {field.value ? format(field.value, "LLL dd, y") : <span>Pick end date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value || undefined}
                          onDayClick={day => {
                            field.onChange(day);
                            setEndPopoverOpen(false);
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                />
                <div className="relative">
                  <Controller
                    control={control}
                    name="endTime"
                    render={({ field }) => (
                      <Input type="time" value={field.value} onChange={field.onChange} className="w-32 pr-12" />
                    )}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                    UTC
                  </span>
                </div>
              </div>
              {fieldErrors.end_date && <p className="text-sm text-destructive mt-1">{fieldErrors.end_date}</p>}
            </div>
          </div>

          <ExperimentVariants
            experimentType={experimentType}
            audiences={audiences}
            audienceOptionsLoading={audienceOptionsLoading}
            predictionFlightsLoading={predictionFlightsLoading}
            campaignFields={campaignFields}
            campaigns={campaigns}
            campaignOptions={campaignOptions}
            appendCampaign={appendCampaign}
            removeCampaign={removeCampaign}
            addCampaignParam={addCampaignParam}
            removeCampaignParam={removeCampaignParam}
            updateCampaignParam={updateCampaignParam}
            totalCampaignTrafficAllocation={totalCampaignTrafficAllocation}
            repoBranchPairs={repoBranchPairs}
            repoBranchPairFields={repoBranchPairFields}
            appendRepoBranchPair={appendRepoBranchPair}
            removeRepoBranchPair={removeRepoBranchPair}
            monitoredModels={monitoredModels}
            predictionFlightFields={predictionFlightFields}
            predictionFlights={predictionFlights}
            predictionFlightOptions={predictionFlightOptions}
            importMetadata={importMetadata}
            appendPredictionFlight={appendPredictionFlight}
            removePredictionFlight={removePredictionFlight}
            addPredictionFlightParam={addPredictionFlightParam}
            removePredictionFlightParam={removePredictionFlightParam}
            updatePredictionFlightParamValue={updatePredictionFlightParamValue}
            updatePredictionFlightParamType={updatePredictionFlightParamType}
            lockPredictionFlights={experimentType === ExperimentType.Global}
            fieldErrors={fieldErrors}
          />

          <div>
            <Label className={EXPERIMENT_FORM_LABEL_CLASS}>Links</Label>
            <div className="space-y-2">
              {externalLinkFields.map((field, index: number) => (
                <div key={field.id} className="flex gap-2 items-center">
                  <Input
                    type="url"
                    value={externalLinks[index]?.url || ""}
                    onChange={e => setValue(`externalLinks.${index}.url`, e.target.value, { shouldDirty: true })}
                    placeholder="https://..."
                    className={fieldErrors.external_links ? "border-destructive" : ""}
                  />
                  <Button type="button" variant="outline" size="icon" onClick={() => removeExternalLink(index)}>
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => appendExternalLink({ url: "" })}
            >
              + Add Link
            </Button>
            {fieldErrors.external_links && (
              <p className="text-sm text-destructive mt-1">{fieldErrors.external_links}</p>
            )}
          </div>

          <div className="flex gap-4">
            {showCancelButton && isEditing && (
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="ml-auto">
                    <Button
                      onClick={handleSave}
                      disabled={(!isDirty && isEditing) || hasBlockingErrors || isLoading || isSaving}
                    >
                      {isSaving ? "Saving..." : isEditing ? "Update Experiment" : "Create Experiment"}
                    </Button>
                  </span>
                </TooltipTrigger>
                {saveBlockReasons.length > 0 && !isSaving && (
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="text-sm font-medium">To save, fix:</p>
                    <ul className="mt-1 list-disc pl-4 text-sm">
                      {saveBlockReasons.map(reason => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        </>
      )}
    </div>
  );
}

export function ExperimentForm({
  experimentId,
  isEditing,
  initialExperiment,
  initialCampaigns,
  onSaveSuccess,
  onCancel,
  showCancelButton = true,
  formMethods,
  showExperimentTypeField = true,
}: ExperimentFormProps) {
  const defaultValues = useMemo(
    () => getExperimentFormDefaultValues(initialExperiment, initialCampaigns),
    [initialExperiment, initialCampaigns],
  );

  const localFormMethods = useForm<ExperimentFormValues>({
    defaultValues,
    values: defaultValues,
  });

  if (formMethods) {
    return (
      <ExperimentFormInner
        experimentId={experimentId}
        isEditing={isEditing}
        onSaveSuccess={onSaveSuccess}
        onCancel={onCancel}
        showCancelButton={showCancelButton}
        showExperimentTypeField={showExperimentTypeField}
      />
    );
  }

  return (
    <FormProvider {...localFormMethods}>
      <ExperimentFormInner
        experimentId={experimentId}
        isEditing={isEditing}
        onSaveSuccess={onSaveSuccess}
        onCancel={onCancel}
        showCancelButton={showCancelButton}
        showExperimentTypeField={showExperimentTypeField}
      />
    </FormProvider>
  );
}
